import React, { PureComponent } from 'react';

import { InjectedIntl, injectIntl } from 'react-intl';

import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { User } from 'src/components/types';

import Description from './description';
import messages from './i18n';
import { DataSection } from './metadata-table';
import * as styles from './styles';
import { MetadataValue } from './types';
import UploadedBy from './uploaded-by';

interface MetadataSection {
  heading: string;
  values: MetadataValue[];
}

export interface MetadataPanelProps {
  intl: InjectedIntl;
  title?: string;
  metadata: MetadataSection[];
  isLoading: boolean;
  // passed from AK media-viewer if panel is being used as a sidebar extension
  close?: () => void;
  description?: string;
  uploadedBy?: User;
  uploadedDate?: string;
}

// validates each object included in the metadata array
function isMetadataSectionValid(section: any): boolean {
  return !!(
    section &&
    section.heading &&
    section.values &&
    Array.isArray(section.values) &&
    section.values.length
  );
}

// only returns section objects that have a valid 'heading' and 'values' array
// @ts-ignore TODO: fix noImplicitAny error here
function getValidMetadataSections(metadata) {
  return metadata && Array.isArray(metadata)
    ? metadata.filter(isMetadataSectionValid)
    : [];
}

class MetadataPanel extends PureComponent<MetadataPanelProps> {
  renderSection(section: MetadataSection, index: number) {
    return (
      <DataSection
        key={index}
        heading={section.heading}
        values={section.values}
      />
    );
  }

  renderContent() {
    const { metadata } = this.props;
    const sections = getValidMetadataSections(metadata);

    if (!sections.length) {
      return null;
    }

    return (
      <section className="metadata-panel-sections">
        {sections.map(this.renderSection)}
      </section>
    );
  }

  render() {
    const {
      intl,
      close,
      title,
      isLoading,
      description,
      uploadedBy,
      uploadedDate,
    } = this.props;

    return (
      <styles.Container>
        <styles.Header>
          {title && <styles.HeaderHeading>{title}</styles.HeaderHeading>}
          {close && (
            <ClosePanelBtn
              onClick={close}
              label={intl.formatMessage(messages.closeButtonLabel)}
            />
          )}
        </styles.Header>
        {isLoading ? (
          <styles.LoadingState>
            <Spinner size="large" />
          </styles.LoadingState>
        ) : (
          <>
            <UploadedBy user={uploadedBy} dateTime={uploadedDate} />
            <Description description={description} intl={intl} />
            {this.renderContent()}
          </>
        )}
      </styles.Container>
    );
  }
}

export default injectIntl(MetadataPanel);

function ClosePanelBtn({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) {
  return (
    <styles.PanelButton
      aria-label={label}
      onClick={onClick}
      iconBefore={
        <ArrowRightIcon
          primaryColor={token('color.icon.inverse', colors.N0)}
          label=""
        />
      }
    />
  );
}
